import React, { useMemo, useState } from "react";

import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { ButtonNew as Button } from "components/buttonNew";
import SelectNew from "components/select/SelectNew";

import { IBusinessProfileForm } from "../../teeOnPaymentsTypes";
import { t } from "i18next";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ICountry, IProvince } from "redux/reducers/models/customer";
import { isBusinessProfileComplete } from "../../useTeeOnPayments";
import {
  isValidPhone,
  isValidPostalCode,
  isValidUrl,
  COMPANY_NAME_MAX_LENGTH,
  ADDRESS_MAX_LENGTH,
} from "../../teeOnPaymentsValidation";

interface IBusinessProfileStepProps {
  values: IBusinessProfileForm;
  countries: ICountry[] | undefined;
  onChange: (nextValues: IBusinessProfileForm) => void;
  onContinue: (values: IBusinessProfileForm) => void;
  onCancel: () => void;
}

export default function BusinessProfileStep(props: IBusinessProfileStepProps) {
  const { values, countries, onChange, onContinue } = props;

  const provinces = useMemo(() => {
    if (!countries || !values.country) {
      return [];
    }
    const country = countries.find(c => c.id === Number(values.country));
    return (country?.provinces ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [countries, values.country]);

  const isContinueDisabled = !isBusinessProfileComplete(values);

  const [dirty, setDirty] = useState({
    businessType: false,
    businessStructure: false,
    legalBusinessName: false,
    operatingName: false,
    registrationNumber: false,
    businessAddress: false,
    city: false,
    country: false,
    province: false,
    postalCode: false,
    phone: false,
    website: false,
  });

  function isNonEmptyString(value: unknown): value is string {
    return typeof value === "string" && value.trim().length > 0;
  }

  function isValidNumericIdString(value: unknown): value is string {
    return isNonEmptyString(value) && Number.isFinite(Number(value));
  }

  const selectedCountry =
    countries && values.country ? countries.find(country => country.id === Number(values.country)) : undefined;
  const isUS = selectedCountry?.code === "US";
  const countryCode = selectedCountry?.code ?? "";

  const isBusinessTypeValid = typeof values.businessType === "number" && Number.isFinite(values.businessType);
  const isBusinessStructureValid = isNonEmptyString(values.businessStructure);
  const isLegalBusinessNameValid =
    isNonEmptyString(values.legalBusinessName) && values.legalBusinessName.trim().length <= COMPANY_NAME_MAX_LENGTH;
  const isOperatingNameValid = isNonEmptyString(values.operatingName);
  const isRegistrationNumberValid = isNonEmptyString(values.registrationNumber);
  const isBusinessAddressValid =
    isNonEmptyString(values.businessAddress) && values.businessAddress.trim().length <= ADDRESS_MAX_LENGTH;
  const isCityValid = isNonEmptyString(values.city);
  const isCountryValid = isValidNumericIdString(values.country);
  const isProvinceValid = isValidNumericIdString(values.province);
  const isPostalCodeValid = countryCode
    ? isValidPostalCode(values.postalCode, countryCode)
    : isNonEmptyString(values.postalCode);
  const isPhoneValid = values.phone.trim().length > 0 ? isValidPhone(values.phone) : false;
  const isWebsiteValid = values.website.trim().length > 0 ? isValidUrl(values.website) : false;

  const business_types = [
    {
      title: t("Membership Clubs, Country Clubs & Private Golf Courses - 7997"),
      value: 7997,
    },
    {
      title: t("Computer Software - 5734"),
      value: 5734,
    },
    {
      title: t("Hotels, Motels, and Resorts - 7011"),
      value: 7011,
    },
    {
      title: t("Sports Clubs / Fields - 7941"),
      value: 7941,
    },
  ];

  const businessStructures = [
    {
      title: t("Corporation"),
      value: "corporation",
    },
    {
      title: t("Partnership"),
      value: "partnership",
    },
  ];

  const helpToolTip = `Your website must:
→ Be viewable and not password-protected 
→ Match the business name you provided.`;

  function updateStringField<
    K extends Exclude<keyof IBusinessProfileForm, "businessType" | "countryCode" | "provinceName">,
  >(field: K, value: string) {
    onChange({
      ...values,
      [field]: value,
    });

    setDirty(prev => ({
      ...prev,
      [field]: prev[field as keyof typeof prev] || value.trim().length > 0,
    }));
  }

  return (
    <Card title="Tell us about your business" subtitle="This information will be used to better serve your business.">
      <Card.Section>
        <div style={{ marginTop: "-24px" }} className="mt-[-24px]">
          <hr />
        </div>
        <p className="text-md text-semibold text-gray-900 mb-3">Business identity</p>

        <div className="grid grid-cols-2 gap-[2rem] mb-[16px]">
          <div className="w-full">
            <SelectNew
              id="business_type"
              label="Business type"
              value={values.businessType ?? (null as any)}
              error={dirty.businessType && !isBusinessTypeValid}
              onChange={(nextBusinessTypeId: number) => {
                onChange({
                  ...values,
                  businessType: nextBusinessTypeId,
                });
                setDirty(prev => ({ ...prev, businessType: true }));
              }}
              options={business_types.map(business => ({ id: business.value, label: business.title }))}
              placeholder={t("Select business type")}
              className="w-full"
            />
          </div>
          <div className="w-full">
            <SelectNew
              id="business_structure"
              label="Business structure"
              value={values.businessStructure}
              error={dirty.businessStructure && !isBusinessStructureValid}
              onChange={(nextBusinessStructure: string) =>
                updateStringField("businessStructure", nextBusinessStructure)
              }
              options={businessStructures.map(business => ({ id: business.value, label: business.title }))}
              placeholder={t("Select business structure")}
            />
          </div>
        </div>

        <Form>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={values.legalBusinessName}
                label="Legal business name"
                id="legal_business_name"
                error={dirty.legalBusinessName && !isLegalBusinessNameValid}
                onChange={e => updateStringField("legalBusinessName", e.target.value)}
                placeholder="Enter legal business name..."
                maxLength={COMPANY_NAME_MAX_LENGTH}
                helpText={t("Must exactly match the name associated with your connected account's business number.")}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={values.operatingName}
                label="Business name"
                id="operating_name"
                error={dirty.operatingName && !isOperatingNameValid}
                onChange={e => updateStringField("operatingName", e.target.value)}
                placeholder="Enter business name..."
                helpText={t("Enter the operating name of the company, if it's different than the legal business name.")}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={values.registrationNumber}
                label="Business registration number"
                id="registration_number"
                error={dirty.registrationNumber && !isRegistrationNumberValid}
                onChange={e => updateStringField("registrationNumber", e.target.value)}
                placeholder="Company registration #"
              />
            </FormLayout.Group>
          </FormLayout>
        </Form>

        <div style={{ marginTop: "-5px", marginBottom: "10px" }}>
          <hr />
        </div>
        <p className="text-md text-semibold text-gray-900 mb-3">Business location</p>

        <Form>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={values.businessAddress}
                label="Business address"
                id="business_address"
                error={dirty.businessAddress && !isBusinessAddressValid}
                onChange={e => updateStringField("businessAddress", e.target.value)}
                placeholder="Enter business address"
                maxLength={ADDRESS_MAX_LENGTH}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={values.city}
                label="City"
                id="business_city"
                error={dirty.city && !isCityValid}
                onChange={e => updateStringField("city", e.target.value)}
                placeholder="Enter city..."
              />
              <SelectNew
                id="business_country"
                label="Country"
                value={values.country ? Number(values.country) : null}
                error={dirty.country && !isCountryValid}
                onChange={(nextCountryId: number) => {
                  const selected = countries?.find(c => c.id === nextCountryId);
                  onChange({
                    ...values,
                    country: String(nextCountryId),
                    countryCode: selected?.code ?? "",
                    province: "",
                    provinceName: "",
                  });

                  setDirty(prev => ({
                    ...prev,
                    country: true,
                    province: false,
                  }));
                }}
                options={(countries ?? []).map((country: ICountry) => ({ id: country.id, label: country.name }))}
                placeholder={t("Select country")}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <div className="grid grid-cols-2 gap-[2rem] ">
                <div className="w-full">
                  <SelectNew
                    id="business_province"
                    label={isUS ? "State" : "Province"}
                    value={values.province ? Number(values.province) : null}
                    error={dirty.province && !isProvinceValid}
                    onChange={(nextProvinceId: number) => {
                      const selected = provinces?.find(p => p.id === nextProvinceId);
                      onChange({
                        ...values,
                        province: String(nextProvinceId),
                        provinceName: selected?.name ?? "",
                      });
                      setDirty(prev => ({ ...prev, province: true }));
                    }}
                    options={provinces.map((province: IProvince) => ({ id: province.id, label: province.name }))}
                    placeholder={isUS ? t("Select state") : t("Select province")}
                    disabled={!values.country}
                  />
                </div>
                <div className="w-full">
                  <Input
                    value={values.postalCode}
                    label={isUS ? "Zip code" : "Postal code"}
                    id="business_postal_code"
                    error={dirty.postalCode && !isPostalCodeValid}
                    onChange={e => updateStringField("postalCode", e.target.value)}
                    placeholder={isUS ? "12345" : "A1A 1A1"}
                    helpText={
                      dirty.postalCode && !isPostalCodeValid
                        ? isUS
                          ? t("Enter a valid US zip code (e.g. 12345)")
                          : t("Enter a valid Canadian postal code (e.g. A1A 1A1)")
                        : undefined
                    }
                  />
                </div>
              </div>
            </FormLayout.Group>
          </FormLayout>
        </Form>

        <div style={{ marginTop: "-5px", marginBottom: "10px" }}>
          <hr />
        </div>
        <p className="text-md text-semibold text-gray-900 mb-3">Public location</p>

        <Form>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={values.phone}
                label="Business phone number"
                id="business_phone"
                error={dirty.phone && !isPhoneValid}
                onChange={e => updateStringField("phone", e.target.value)}
                placeholder="+1 (555) 123-4567"
                helpText={
                  dirty.phone && !isPhoneValid ? t("Enter a valid phone number with at least 10 digits") : undefined
                }
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={values.website}
                label="Business website"
                id="business_website"
                error={dirty.website && !isWebsiteValid}
                onChange={e => updateStringField("website", e.target.value)}
                placeholder="www.example.com"
                prefix="https://"
                suffix={
                  <>
                    <OverlayTrigger
                      placement="left-start"
                      overlay={
                        <Tooltip className="tee-on-payments-wide-tooltip" id="">
                          {helpToolTip}
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon className="" icon={faQuestionCircle} />
                    </OverlayTrigger>
                  </>
                }
              />
            </FormLayout.Group>
          </FormLayout>
        </Form>
      </Card.Section>

      <Card.Section>
        <Button
          type="primary"
          className="w-full justify-center"
          disabled={isContinueDisabled}
          onClick={() => {
            if (isContinueDisabled) {
              return;
            }

            onContinue(values);
          }}
        >
          Save and continue
        </Button>
      </Card.Section>
    </Card>
  );
}
